import React, { useState, useEffect, forwardRef, useImperativeHandle, useCallback, useMemo, useRef } from 'react'
import { Form, Row, Col } from 'antd'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { useSelector, useDispatch } from 'react-redux'
import { useRightMenu } from '../../../../core/contexts/RightMenuContext.jsx'
import { useDrawer } from '../../../../core/contexts/DrawerContext.jsx'
import { useLocation } from 'react-router-dom'
import { updateActiveRecord } from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectLookupOptions, useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { FieldTypeEnum, type CheckboxFieldConfig } from '../../../../core/components/InputManager/types.ts'
import { buildAccountRoleOptions } from '../../../../core/utils/accountRoleOptions.ts'
import { AccountRecordFields } from '../../../../state_manager/data_models/account_record.ts'
import { getCompanyInfoFields, getBusinessAddressFields, getOfficeAddressFields } from '../../utils/fieldConfigs.ts'
import { handleFieldChange as handleBusinessRules } from '../../utils/companyFormBusinessRules.ts'
import { useCompaniesForm } from '../../hooks/useCompaniesForm.ts'
import { CompanyTeamTable } from '../../../../core/components/CompanyTeamTable/index.tsx'
import { AccountRecord } from '../../../../state_manager/data_models/account_record.ts'
import { LOOKUP_CATEGORY_INDUSTRY } from '../../../../state_manager/constants/lookupCategories.ts'
import { FIELD_KEY_LABEL } from '../../../../state_manager/globals.ts'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG } from '../../../../core/components/FormTable/constants.ts'
import { companyTeamEntryToMember, type TeamMember } from '../../../../core/components/CompanyTeamTable/utils/utils.ts'

// A seed carries its team either as the drawer's own rows (`companyTeamMembers`,
// restored by the quick-add overlay) or as the canonical record's
// `prog__company_team` (what process_new_account consumes, and so what an
// agent create_company proposal holds). Only the rows were read, so a
// proposal's contacts never reached the table and approve sent an empty team.
// Returns null when the seed carries no team, and the seed minus both keys —
// they are collections, not field state.
const splitCompanySeed = (
  company: Record<string, any>
): { teamMembers: TeamMember[] | null; companyData: Record<string, any> } => {
  const { companyTeamMembers, prog__company_team: recordTeam, ...companyData } = company
  const rawTeam = Array.isArray(companyTeamMembers) ? companyTeamMembers : recordTeam
  return {
    teamMembers: Array.isArray(rawTeam) ? rawTeam.map(companyTeamEntryToMember) : null,
    companyData
  }
}

export const Drawer = forwardRef<
  { submit: () => Promise<void> },
  { company?: AccountRecord | null; onSubmit?: (data: any) => Promise<void>; loading?: boolean }
>(({ company = null, onSubmit, loading = false }, ref) => {
  const dispatch = useDispatch()
  const { isNew, isOpen: drawerIsOpen } = useDrawer()
  const location = useLocation()
  const { setRightMenuWidth, resetRightMenuWidth } = useRightMenu()
  const [form] = Form.useForm()

  const tempRecordUuid = useMemo(() => {
    if (isNew) {
      return 'companies-new'
    }
    return null
  }, [isNew])

  // Drawer-variant layout: true for the URL-driven module drawer AND for the
  // global quick-add overlay (whose DrawerContext override sets isOpen).
  const isDrawerContext = drawerIsOpen || location.pathname.includes('/forms/drawer/')
  const { getLookupLabel, getLookupIdByLabel, getLookupOptions } = useLookups()

  const industryOptions = useSelector((state: any) => selectLookupOptions(state, LOOKUP_CATEGORY_INDUSTRY))
  const accountRoleChoices = useSelector((state: any) => state.globalConfigs?.account_role_choices || [])
  const investorClassifications = useSelector((state: any) => state.globalConfigs?.investor_classifications || [])
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const getRoleOptions = useCallback(
    () => buildAccountRoleOptions(accountRoleChoices),
    [accountRoleChoices]
  )

  const getInvestorClassificationOptions = useCallback(() => {
    return investorClassifications.map((classification: any) => ({
      value: classification.id,
      label: classification.label
    }))
  }, [investorClassifications])

  const getInitialFormData = useCallback(
    (): Partial<AccountRecord> => ({
      [AccountRecordFields.accounts__id]: crypto.randomUUID(),
      [AccountRecordFields.accounts__name]: '',
      [AccountRecordFields.accounts__legal_name]: '',
      prog__account_roles: [],
      [AccountRecordFields.accounts__industry]: '',
      [AccountRecordFields.accounts__investor_classification_id]: null,
      [AccountRecordFields.accounts__website]: '',
      [AccountRecordFields.accounts__description]: '',
      [AccountRecordFields.accounts__business_address_1]: '',
      [AccountRecordFields.accounts__business_address_2]: '',
      [AccountRecordFields.accounts__business_city_label]: '',
      [AccountRecordFields.accounts__business_stateprovince]: '',
      [AccountRecordFields.accounts__business_postalcode]: '',
      [AccountRecordFields.accounts__business_country_label]: '',
      [AccountRecordFields.accounts__same_as_business_address]: true,
      [AccountRecordFields.accounts__office_address_1]: '',
      [AccountRecordFields.accounts__office_address_2]: '',
      [AccountRecordFields.accounts__office_city_code__label]: '',
      [AccountRecordFields.accounts__office_stateprovince]: '',
      [AccountRecordFields.accounts__office_postalcode]: '',
      [AccountRecordFields.accounts__office_country_code__label]: ''
    }),
    []
  )

  const [formData, setFormData] = useState<Record<string, any>>(() => {
    if (company && Object.keys(company).length > 0) {
      return { ...getInitialFormData(), ...splitCompanySeed(company).companyData }
    }
    return getInitialFormData()
  })

  // Seeded on the first render: CompanyTeamTable reads initialTeamMembers once.
  const [companyTeamMembers, setCompanyTeamMembers] = useState<TeamMember[]>(
    () => (company && splitCompanySeed(company).teamMembers) || []
  )

  const getTeamMembersForAPIRef = useRef<((recordId: string) => any[]) | null>(null)

  const companyTeamRef = useRef<{ hasPendingTeamMembers: () => boolean } | null>(null)

  const isUpdatingTeamMembersFromTableRef = useRef(false)

  useEffect(() => {
    if (isUpdatingTeamMembersFromTableRef.current) {
      const timeoutId = setTimeout(() => {
        isUpdatingTeamMembersFromTableRef.current = false
        if (tempRecordUuid && isNew) {
          const currentData = {
            ...formData,
            companyTeamMembers
          }
          dispatch(
            updateActiveRecord({
              uuid: tempRecordUuid,
              updates: currentData
            })
          )
        }
      }, 100)
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [companyTeamMembers, tempRecordUuid, isNew, formData, dispatch])

  const { transformFormDataToAccountRecord, validateRequiredFields } = useCompaniesForm(
    getLookupIdByLabel,
    getLookupLabel,
    getLookupOptions
  )

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        if (category === LOOKUP_CATEGORY_INDUSTRY) return industryOptions
        return []
      },
      getRoleOptions,
      getInvestorClassificationOptions
    }),
    [industryOptions, getRoleOptions, getInvestorClassificationOptions]
  )

  const isInitializingRef = useRef(false)
  const lastSyncedDataRef = useRef<string | null>(null)

  const formDataRef = useRef(formData)
  const companyTeamMembersRef = useRef(companyTeamMembers)

  useEffect(() => {
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
      syncTimeoutRef.current = null
    }

    if (isNew && company && Object.keys(company).length > 0) {
      isInitializingRef.current = true
      const initialData = getInitialFormData()
      const { teamMembers: restoredTeamMembers, companyData: companyDataWithoutTeam } = splitCompanySeed(company)

      const mergedData = { ...initialData, ...companyDataWithoutTeam }

      formDataRef.current = mergedData
      if (restoredTeamMembers) companyTeamMembersRef.current = restoredTeamMembers

      setFormData(mergedData)
      form.setFieldsValue(mergedData)

      if (restoredTeamMembers) {
        setCompanyTeamMembers(restoredTeamMembers)
      }

      lastSyncedDataRef.current = JSON.stringify({
        ...mergedData,
        companyTeamMembers: restoredTeamMembers || []
      })

      setTimeout(() => {
        isInitializingRef.current = false
      }, 100)
    } else if (isNew && (!company || Object.keys(company).length === 0)) {
      const initialData = getInitialFormData()
      setFormData(initialData)
      form.setFieldsValue(initialData)
      setCompanyTeamMembers([])
      lastSyncedDataRef.current = JSON.stringify({
        ...initialData,
        companyTeamMembers: []
      })
    }
  }, [company, isNew, form, getInitialFormData, tempRecordUuid])

  useEffect(() => {
    formDataRef.current = formData
  }, [formData])
  useEffect(() => {
    companyTeamMembersRef.current = companyTeamMembers
  }, [companyTeamMembers])

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if (!tempRecordUuid || !isNew || isInitializingRef.current || isUpdatingTeamMembersFromTableRef.current) {
      return
    }

    const currentData = {
      ...formDataRef.current,
      companyTeamMembers: companyTeamMembersRef.current
    }
    const currentDataString = JSON.stringify(currentData)

    if (currentDataString === lastSyncedDataRef.current) {
      return
    }

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current)
    }

    syncTimeoutRef.current = setTimeout(() => {
      if (!isInitializingRef.current && !isUpdatingTeamMembersFromTableRef.current) {
        const latestData = {
          ...formDataRef.current,
          companyTeamMembers: companyTeamMembersRef.current
        }
        const latestDataString = JSON.stringify(latestData)

        dispatch(
          updateActiveRecord({
            uuid: tempRecordUuid,
            updates: latestData
          })
        )
        lastSyncedDataRef.current = latestDataString
      }
    }, 300)

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [formData, companyTeamMembers, tempRecordUuid, isNew, dispatch])

  useEffect(() => {
    if (isDrawerContext) {
      setRightMenuWidth('42%')
      return () => {
        resetRightMenuWidth()
      }
    }
    return undefined
  }, [isDrawerContext, setRightMenuWidth, resetRightMenuWidth])

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      setFormData((prev) => {
        const businessRuleResult = handleBusinessRules(field, value, prev, form)
        let newData = businessRuleResult.formData

        if (
          businessRuleResult.formData[AccountRecordFields.accounts__business_country_code] &&
          countriesData.length > 0
        ) {
          const countryCode = businessRuleResult.formData[AccountRecordFields.accounts__business_country_code]
          const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryLabel) {
            const countryLabelField = `${AccountRecordFields.accounts__business_country_code}${FIELD_KEY_LABEL}`
            newData[countryLabelField] = countryLabel
            if (businessRuleResult.formUpdates) {
              businessRuleResult.formUpdates[countryLabelField] = countryLabel
            } else {
              businessRuleResult.formUpdates = { [countryLabelField]: countryLabel }
            }
          }
        }

        if (businessRuleResult.formUpdates) {
          form.setFieldsValue(businessRuleResult.formUpdates)
        }

        setValidationErrors((prevErrors) => {
          const updatedErrors = { ...prevErrors }
          if (updatedErrors.companyInfo?.[field]) {
            updatedErrors.companyInfo = { ...updatedErrors.companyInfo }
            delete updatedErrors.companyInfo[field]
          }
          if (updatedErrors.businessAddress?.[field]) {
            updatedErrors.businessAddress = { ...updatedErrors.businessAddress }
            delete updatedErrors.businessAddress[field]
          }
          if (updatedErrors.officeAddress?.[field]) {
            updatedErrors.officeAddress = { ...updatedErrors.officeAddress }
            delete updatedErrors.officeAddress[field]
          }
          return updatedErrors
        })

        if (field === AccountRecordFields.accounts__same_as_business_address && value === true) {
          newData[AccountRecordFields.accounts__office_address_1] =
            prev[AccountRecordFields.accounts__business_address_1]
          newData[AccountRecordFields.accounts__office_address_2] =
            prev[AccountRecordFields.accounts__business_address_2]
          newData[AccountRecordFields.accounts__office_stateprovince] =
            prev[AccountRecordFields.accounts__business_stateprovince]
          newData[AccountRecordFields.accounts__office_postalcode] =
            prev[AccountRecordFields.accounts__business_postalcode]
          form.setFieldsValue({
            [AccountRecordFields.accounts__office_address_1]: prev[AccountRecordFields.accounts__business_address_1],
            [AccountRecordFields.accounts__office_address_2]: prev[AccountRecordFields.accounts__business_address_2],
            [AccountRecordFields.accounts__office_stateprovince]:
              prev[AccountRecordFields.accounts__business_stateprovince],
            [AccountRecordFields.accounts__office_postalcode]: prev[AccountRecordFields.accounts__business_postalcode]
          })
        }

        formDataRef.current = newData

        return newData
      })
    },
    [form]
  )

  const handleSubmit = useCallback(async () => {
    try {
      const hasPendingTeamMembers = companyTeamRef.current?.hasPendingTeamMembers?.() || false
      if (hasPendingTeamMembers) {
        showErrorToast('Please save or remove any pending company team members before creating the company', TOAST_CONFIG)
        return
      }

      const validation = validateRequiredFields(formData)
      if (!validation.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          companyInfo: {
            ...prev.companyInfo,
            ...validation.errors
          }
        }))
        const firstErrorField = Object.keys(validation.errors)[0]
        if (firstErrorField) {
          const errorElement = document.querySelector(`[data-field-key="${firstErrorField}"]`)
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        }
        return
      }

      let apiFormattedTeamMembers: any[] = []
      if (getTeamMembersForAPIRef.current) {
        const tempRecordId = crypto.randomUUID()
        apiFormattedTeamMembers = getTeamMembersForAPIRef.current(tempRecordId)
      }

      const accountData = transformFormDataToAccountRecord(formData)

      const companyData = {
        ...accountData,
        prog__company_team: apiFormattedTeamMembers
      }

      setValidationErrors({
        companyInfo: {},
        businessAddress: {},
        officeAddress: {}
      })

      if (onSubmit) {
        await onSubmit(companyData)
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Form submission failed:', error)
      }
      throw error
    }
  }, [formData, onSubmit, validateRequiredFields, transformFormDataToAccountRecord])

  useImperativeHandle(
    ref,
    () => ({
      submit: handleSubmit
    }),
    [handleSubmit]
  )

  const businessCountryCode = formData[AccountRecordFields.accounts__business_country_code]
  const officeCountryCode = formData[AccountRecordFields.accounts__office_country_code]
  const companyInfoFields = useMemo(() => getCompanyInfoFields(formData, helpers), [formData, helpers])
  const businessAddressFields = useMemo(
    () => getBusinessAddressFields(formData, helpers),
    [formData, businessCountryCode, helpers]
  )
  const officeAddressFields = useMemo(
    () => getOfficeAddressFields(formData, helpers),
    [formData, officeCountryCode, helpers]
  )

  const checkboxField = useMemo<CheckboxFieldConfig>(
    () => ({
      key: AccountRecordFields.accounts__same_as_business_address,
      type: FieldTypeEnum.CHECKBOX,
      label: '',
      value:
        formData[AccountRecordFields.accounts__same_as_business_address] !== undefined
          ? formData[AccountRecordFields.accounts__same_as_business_address]
          : true,
      checkboxLabel: 'Same as business address',
      editable: true
    }),
    [formData]
  )

  const [validationErrors, setValidationErrors] = useState<Record<string, Record<string, string>>>({
    companyInfo: {},
    businessAddress: {},
    officeAddress: {}
  })

  return (
    <div
      data-debug='drawer-new-container'
      data-component='CompanyDrawerNew'
      data-is-new={isNew}
      data-company-id={company?.[AccountRecordFields.accounts__id]}
      className='space-y-1'
    >
      <Form
        data-debug='drawer-new-form'
        form={form}
        layout='vertical'
        className='form-compact'
        initialValues={formData}
        onValuesChange={(changedValues) => {
          Object.keys(changedValues).forEach((key) => {
            handleFieldChange(key, changedValues[key])
          })
        }}
      >
        {isDrawerContext ? (
          <div className='space-y-4'>
            <FormSection title='Company Information' className='mb-2'>
              <InputManager
                inputMode='edit'
                labelPosition='above'
                fields={companyInfoFields}
                onChange={(field, value) => handleFieldChange(field, value)}
                validationErrors={validationErrors.companyInfo}
              />
            </FormSection>

            <FormSection title='Business Address' className='mb-2'>
              <InputManager
                inputMode='edit'
                labelPosition='above'
                fields={businessAddressFields}
                onChange={(field, value) => handleFieldChange(field, value)}
                validationErrors={validationErrors.businessAddress}
              />
            </FormSection>

            <FormSection title='Registered Office' className='mb-2'>
              <div>
                <div className='mb-3'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='above'
                    fields={[checkboxField]}
                    onChange={(field, value) => handleFieldChange(field, value)}
                  />
                </div>

                {!formData[AccountRecordFields.accounts__same_as_business_address] && (
                  <InputManager
                    inputMode='edit'
                    labelPosition='above'
                    fields={officeAddressFields}
                    onChange={(field, value) => handleFieldChange(field, value)}
                    validationErrors={validationErrors.officeAddress}
                  />
                )}
              </div>
            </FormSection>

            <div className='mt-2'>
              <CompanyTeamTable
                ref={companyTeamRef}
                mode='new'
                initialTeamMembers={companyTeamMembers}
                onChange={(updatedTeamMembers) => {
                  isUpdatingTeamMembersFromTableRef.current = true
                  setCompanyTeamMembers(updatedTeamMembers)
                }}
                getCurrentTeamMembers={(getApiDataFn) => {
                  getTeamMembersForAPIRef.current = getApiDataFn
                }}
              />
            </div>
          </div>
        ) : (
          <>
            <Row data-debug='drawer-new-main-row' gutter={12}>
              <Col span={12}>
                <FormSection title='Company Information' className='mb-2'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='above'
                    fields={companyInfoFields}
                    onChange={(field, value) => handleFieldChange(field, value)}
                    validationErrors={validationErrors.companyInfo}
                  />
                </FormSection>
              </Col>

              <Col span={12}>
                <FormSection title='Business Address' className='mb-2'>
                  <InputManager
                    inputMode='edit'
                    labelPosition='above'
                    fields={businessAddressFields}
                    onChange={(field, value) => handleFieldChange(field, value)}
                    validationErrors={validationErrors.businessAddress}
                  />
                </FormSection>

                <FormSection title='Registered Office' className='mb-2'>
                  <div>
                    <div className='mb-3'>
                      <InputManager
                        inputMode='edit'
                        labelPosition='above'
                        fields={[checkboxField]}
                        onChange={(field, value) => handleFieldChange(field, value)}
                      />
                    </div>

                    {!formData[AccountRecordFields.accounts__same_as_business_address] && (
                      <InputManager
                        inputMode='edit'
                        labelPosition='above'
                        fields={officeAddressFields}
                        onChange={(field, value) => handleFieldChange(field, value)}
                        validationErrors={validationErrors.officeAddress}
                      />
                    )}
                  </div>
                </FormSection>
              </Col>
            </Row>

            <Row data-debug='drawer-new-company-team-row' className='mt-2'>
              <Col span={24}>
                <CompanyTeamTable
                  ref={companyTeamRef}
                  mode='new'
                  initialTeamMembers={companyTeamMembers}
                  onChange={(updatedTeamMembers) => {
                    isUpdatingTeamMembersFromTableRef.current = true
                    setCompanyTeamMembers(updatedTeamMembers)
                  }}
                  getCurrentTeamMembers={(getApiDataFn) => {
                    getTeamMembersForAPIRef.current = getApiDataFn
                  }}
                />
              </Col>
            </Row>
          </>
        )}
      </Form>
    </div>
  )
})

Drawer.displayName = 'CompanyDrawerNew'
